import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="address-autocomplete"
// Utilise l'API gratuite de la Géoplateforme IGN (https://geoservices.ign.fr)
export default class extends Controller {
  static targets = ["input", "results"]

  connect() {
    this.timeout = null
    this.abortController = null
    this.selectedIndex = -1

    // Créer le conteneur de résultats s'il n'existe pas
    if (!this.hasResultsTarget) {
      this.createResultsContainer()
    }

    // Fermer les résultats lors d'un clic extérieur
    document.addEventListener("click", this.handleClickOutside.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside.bind(this))
    if (this.timeout) clearTimeout(this.timeout)
    if (this.abortController) this.abortController.abort()
  }

  createResultsContainer() {
    const container = document.createElement("div")
    container.setAttribute("data-address-autocomplete-target", "results")
    container.className = "absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto hidden"
    this.inputTarget.parentNode.style.position = "relative"
    this.inputTarget.parentNode.style.zIndex = "50"
    this.inputTarget.parentNode.appendChild(container)
  }

  // Appelé lors de la saisie
  search() {
    const query = this.inputTarget.value.trim()

    // Annuler la recherche précédente
    if (this.timeout) clearTimeout(this.timeout)
    if (this.abortController) this.abortController.abort()

    // Masquer les résultats si moins de 3 caractères
    if (query.length < 3) {
      this.hideResults()
      return
    }

    // Debounce de 300ms
    this.timeout = setTimeout(() => {
      this.fetchSuggestions(query)
    }, 300)
  }

  async fetchSuggestions(query) {
    this.abortController = new AbortController()

    try {
      const params = new URLSearchParams({
        q: query,
        limit: "7",
        autocomplete: "1"
      })

      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?${params}`,
        { signal: this.abortController.signal }
      )

      if (!response.ok) throw new Error("Erreur API")

      const data = await response.json()
      this.displayResults(data.features || [])
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Erreur autocomplétion:", error)
        this.hideResults()
      }
    }
  }

  displayResults(results) {
    if (!this.hasResultsTarget) return

    if (results.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-3 text-sm text-gray-500">
          Aucune adresse trouvée
        </div>
      `
      this.showResults()
      return
    }

    this.selectedIndex = -1
    this.resultsTarget.innerHTML = results.map((result, index) => {
      const label = result.properties.name || ""
      const city = result.properties.city || ""
      const postcode = result.properties.postcode || ""
      const context = result.properties.context || ""
      const subtitle = [postcode, city, context].filter(Boolean).join(" - ")
      const fullAddress = result.properties.label || ""

      return `
        <div class="px-4 py-3 cursor-pointer hover:bg-brand-500/10 transition-colors border-b border-gray-100 last:border-0"
             data-index="${index}"
             data-address="${this.escapeHtml(fullAddress)}"
             data-action="click->address-autocomplete#selectResult mouseenter->address-autocomplete#highlightResult">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-brand-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <div class="min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">${this.escapeHtml(label)}</p>
              ${subtitle ? `<p class="text-xs text-gray-500">${this.escapeHtml(subtitle)}</p>` : ""}
            </div>
          </div>
        </div>
      `
    }).join("")

    this.showResults()
  }

  selectResult(event) {
    const address = event.currentTarget.dataset.address
    this.inputTarget.value = address
    this.hideResults()

    // Déclencher un événement pour informer d'autres scripts
    const customEvent = new CustomEvent("address-selected", {
      detail: { address },
      bubbles: true
    })
    this.element.dispatchEvent(customEvent)
  }

  highlightResult(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.updateHighlight(index)
  }

  // Navigation au clavier
  navigate(event) {
    if (!this.resultsTarget || this.resultsTarget.classList.contains("hidden")) {
      return
    }

    const items = this.resultsTarget.querySelectorAll("[data-index]")
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.updateHighlight(this.selectedIndex)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateHighlight(this.selectedIndex)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          const address = items[this.selectedIndex].dataset.address
          this.inputTarget.value = address
          this.hideResults()
        }
        break
      case "Escape":
        this.hideResults()
        break
    }
  }

  updateHighlight(index) {
    const items = this.resultsTarget.querySelectorAll("[data-index]")
    items.forEach((item, i) => {
      if (i === index) {
        item.classList.add("bg-brand-500/10")
        item.scrollIntoView({ block: "nearest" })
      } else {
        item.classList.remove("bg-brand-500/10")
      }
    })
    this.selectedIndex = index
  }

  showResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove("hidden")
    }
  }

  hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
    }
    this.selectedIndex = -1
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
